import { useState } from "react";
import emailjs from "@emailjs/browser";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Check, Github, Loader2, Mail } from "lucide-react";
import SectionReveal from "@/components/effects/SectionReveal";
import MagneticButton from "@/components/effects/MagneticButton";
import { PROFILE } from "@/data/content";
import { TID } from "@/constants/testIds";

const EMAILJS = {
    serviceId: process.env.REACT_APP_EMAILJS_SERVICE_ID,
    contactTemplate: process.env.REACT_APP_EMAILJS_CONTACT_TEMPLATE_ID,
    autoReplyTemplate: process.env.REACT_APP_EMAILJS_AUTOREPLY_TEMPLATE_ID,
    publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY
};

const initialForm = { name: "", email: "", subject: "", message: "" };

const Contact = () => {
    const navigate = useNavigate();
    const [form, setForm] = useState(initialForm);
    const [status, setStatus] = useState({ state: "idle", message: "" });

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const isConfigured = !!(EMAILJS.serviceId && EMAILJS.contactTemplate && EMAILJS.publicKey);

    const submit = async (e) => {
        e.preventDefault();
        if (status.state === "loading") return;

        if (!form.name || !form.email || !form.message) {
            setStatus({ state: "error", message: "Please fill in your name, email, and message." });
            return;
        }
        if (!isConfigured) {
            setStatus({
                state: "error",
                message:
                    "Contact form is not configured yet. Please email mohammadnaved925@gmail.com directly, or set the EmailJS environment variables."
            });
            return;
        }

        setStatus({ state: "loading", message: "" });
        try {
            const templateParams = {
                from_name: form.name,
                from_email: form.email,
                reply_to: form.email,
                subject: form.subject || "New enquiry from mohammadnaved.com",
                message: form.message,
                to_email: PROFILE.email,
                site: "mohammadnaved.com"
            };

            // 1) Send to Mohammad
            await emailjs.send(EMAILJS.serviceId, EMAILJS.contactTemplate, templateParams, {
                publicKey: EMAILJS.publicKey
            });

            // 2) Auto-reply to the visitor (optional)
            if (EMAILJS.autoReplyTemplate) {
                try {
                    await emailjs.send(EMAILJS.serviceId, EMAILJS.autoReplyTemplate, templateParams, {
                        publicKey: EMAILJS.publicKey
                    });
                } catch (autoErr) {
                    // Auto-reply failure is non-fatal for the visitor.
                    console.warn("Auto-reply failed:", autoErr);
                }
            }

            setStatus({ state: "success", message: "Message sent — redirecting…" });
            setForm(initialForm);
            setTimeout(() => navigate("/thank-you"), 1400);
        } catch (err) {
            console.error(err);
            setStatus({
                state: "error",
                message: "Something went wrong sending your message. Please email mohammadnaved925@gmail.com directly."
            });
        }
    };

    return (
        <section id="contact" className="relative py-24 md:py-40">
            <div className="container-x">
                <div className="grid gap-12 md:grid-cols-12">
                    <SectionReveal className="md:col-span-5">
                        <p className="overline">08 — Contact</p>
                        <h2
                            className="mt-4 text-3xl font-medium tracking-tight text-white sm:text-4xl md:text-5xl"
                            style={{ fontFamily: "'General Sans', sans-serif" }}
                        >
                            Let&apos;s build something worth building.
                        </h2>
                        <p className="mt-6 max-w-md text-base text-brand-muted md:text-lg">
                            Have a project, a role, or an idea to talk through? Send a quick note — I read everything
                            personally and reply within a business day.
                        </p>

                        <div className="mt-10 space-y-4">
                            <a
                                data-testid={TID.contactEmailLink}
                                href={`mailto:${PROFILE.email}`}
                                className="group flex items-center gap-4 rounded-2xl border border-white/[.08] bg-[#0a0a0a] p-5 transition-all hover:border-white/20 hover:bg-[#0f0f0f]"
                            >
                                <span className="grid h-11 w-11 place-items-center rounded-xl border border-white/[.08] bg-white/[.03] text-white group-hover:text-brand-primary">
                                    <Mail className="h-5 w-5" />
                                </span>
                                <div className="flex-1">
                                    <p className="text-xs uppercase tracking-widest text-brand-muted">Email</p>
                                    <p className="text-sm font-medium text-white">{PROFILE.email}</p>
                                </div>
                                <ArrowUpRight className="h-4 w-4 text-brand-muted transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                            </a>
                            <a
                                data-testid={TID.contactGithubLink}
                                href={PROFILE.github}
                                target="_blank"
                                rel="noreferrer"
                                className="group flex items-center gap-4 rounded-2xl border border-white/[.08] bg-[#0a0a0a] p-5 transition-all hover:border-white/20 hover:bg-[#0f0f0f]"
                            >
                                <span className="grid h-11 w-11 place-items-center rounded-xl border border-white/[.08] bg-white/[.03] text-white group-hover:text-brand-primary">
                                    <Github className="h-5 w-5" />
                                </span>
                                <div className="flex-1">
                                    <p className="text-xs uppercase tracking-widest text-brand-muted">GitHub</p>
                                    <p className="text-sm font-medium text-white">github.com/Mn2902</p>
                                </div>
                                <ArrowUpRight className="h-4 w-4 text-brand-muted transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                            </a>
                        </div>
                    </SectionReveal>

                    <SectionReveal delay={0.15} className="md:col-span-7">
                        <form
                            onSubmit={submit}
                            className="relative overflow-hidden rounded-3xl border border-white/[.08] bg-[#0a0a0a] p-6 md:p-10"
                            noValidate
                        >
                            <div
                                aria-hidden
                                className="pointer-events-none absolute inset-0 opacity-40"
                                style={{
                                    background:
                                        "radial-gradient(120% 60% at 90% -10%, rgba(59,130,246,0.14), transparent 60%), radial-gradient(80% 60% at -10% 110%, rgba(6,182,212,0.10), transparent 60%)"
                                }}
                            />
                            <div className="relative grid gap-5 md:grid-cols-2">
                                <div className="md:col-span-1">
                                    <label className="mb-2 block text-xs font-medium uppercase tracking-widest text-brand-muted">
                                        Your name
                                    </label>
                                    <input
                                        data-testid={TID.contactName}
                                        name="name"
                                        value={form.name}
                                        onChange={onChange}
                                        required
                                        autoComplete="name"
                                        placeholder="Ada Lovelace"
                                        className="w-full rounded-xl border border-white/[.08] bg-black/40 px-4 py-3 text-white placeholder-brand-muted transition-colors focus:border-brand-primary focus:outline-none"
                                    />
                                </div>
                                <div className="md:col-span-1">
                                    <label className="mb-2 block text-xs font-medium uppercase tracking-widest text-brand-muted">
                                        Email
                                    </label>
                                    <input
                                        data-testid={TID.contactEmail}
                                        type="email"
                                        name="email"
                                        value={form.email}
                                        onChange={onChange}
                                        required
                                        autoComplete="email"
                                        placeholder="you@company.com"
                                        className="w-full rounded-xl border border-white/[.08] bg-black/40 px-4 py-3 text-white placeholder-brand-muted transition-colors focus:border-brand-primary focus:outline-none"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="mb-2 block text-xs font-medium uppercase tracking-widest text-brand-muted">
                                        Subject
                                    </label>
                                    <input
                                        data-testid={TID.contactSubject}
                                        name="subject"
                                        value={form.subject}
                                        onChange={onChange}
                                        placeholder="Website · eCommerce · Consulting"
                                        className="w-full rounded-xl border border-white/[.08] bg-black/40 px-4 py-3 text-white placeholder-brand-muted transition-colors focus:border-brand-primary focus:outline-none"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="mb-2 block text-xs font-medium uppercase tracking-widest text-brand-muted">
                                        Message
                                    </label>
                                    <textarea
                                        data-testid={TID.contactMessage}
                                        name="message"
                                        value={form.message}
                                        onChange={onChange}
                                        required
                                        rows={6}
                                        placeholder="Tell me a little about the project — what you're building and what success looks like."
                                        className="w-full resize-y rounded-xl border border-white/[.08] bg-black/40 px-4 py-3 text-white placeholder-brand-muted transition-colors focus:border-brand-primary focus:outline-none"
                                    />
                                </div>
                            </div>

                            <div className="relative mt-8 flex flex-col-reverse items-start justify-between gap-4 sm:flex-row sm:items-center">
                                <p className="text-xs text-brand-muted">
                                    By sending, you agree that Mohammad may reply to your email.
                                </p>
                                <MagneticButton
                                    as="button"
                                    type="submit"
                                    data-testid={TID.contactSubmit}
                                    disabled={status.state === "loading"}
                                    className="btn-primary disabled:cursor-not-allowed disabled:opacity-70"
                                >
                                    {status.state === "loading" ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Sending
                                        </>
                                    ) : (
                                        <>
                                            Send message
                                            <ArrowUpRight className="h-4 w-4" />
                                        </>
                                    )}
                                </MagneticButton>
                            </div>

                            <AnimatePresence>
                                {status.state === "success" && (
                                    <motion.div
                                        data-testid={TID.contactSuccess}
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -8 }}
                                        className="relative mt-6 inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-4 py-2 text-sm text-emerald-300"
                                    >
                                        <Check className="h-4 w-4" />
                                        {status.message || "Message sent."}
                                    </motion.div>
                                )}
                                {status.state === "error" && (
                                    <motion.div
                                        data-testid={TID.contactError}
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -8 }}
                                        className="relative mt-6 inline-flex items-center gap-2 rounded-full border border-rose-400/30 bg-rose-400/10 px-4 py-2 text-sm text-rose-300"
                                    >
                                        {status.message}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </form>
                    </SectionReveal>
                </div>
            </div>
        </section>
    );
};

export default Contact;
